function _classPrivateMethodInitSpec(e, a) { _checkPrivateRedeclaration(e, a), a.add(e); }
function _checkPrivateRedeclaration(e, t) { if (t.has(e)) throw new TypeError("Cannot initialize the same private elements twice on an object"); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _classPrivateMethodGet(s, a, r) { return _assertClassBrand(a, s), r; }
function _assertClassBrand(e, t, n) { if ("function" == typeof e ? e === t : e.has(t)) return arguments.length < 3 ? t : n; throw new TypeError("Private element is not present on this object"); }
import EmberObject from '@ember/object';
import { DeviceProfileType } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/model/shared/device_profile_type';
import { ProductType } from '@square/dashboard-protos/models/squareup/devicesettings/product_type';
import { Device } from '@square/dashboard-protos/models/squareup/client/device';
import { ApplicationType } from '@square/dashboard-protos/models/squareup/devices/external/application_type';
import { TenderSettings } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/model/settings/common/payment_types';
import { FirstPartyAddon } from '@square/dashboard-protos/models/squareup/addons/shared/first_party_addons';
import { KDSFetchSettings } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/model/settings/kds/kds_fetch_settings';
const DeviceProduct = Device.Product;
var _getAddonSettingsNotAvailableOnMobileClient = /*#__PURE__*/new WeakSet();
var _getAddonSettingsNotAvailableOnCurrentSubscription = /*#__PURE__*/new WeakSet();
var _getSettingsNotAvailableInCurrentCountry = /*#__PURE__*/new WeakSet();
var _getSettingsNotAvailableOnMobileClient = /*#__PURE__*/new WeakSet();
var _getSettingsNotAvailableOnCurrentSubscription = /*#__PURE__*/new WeakSet();
export default class VerticalConfiguration extends EmberObject {
  constructor(...args) {
    super(...args);
    /**
     * @return {Array<string>} Array of setting keys in the proto that should be hidden if the seller does not meet the subscription
     * requirements
     */
    _classPrivateMethodInitSpec(this, _getSettingsNotAvailableOnCurrentSubscription);
    /**
     * @return {Array<string>} Array of setting keys in the proto that are intended to be on DPv2 but should be hidden
     * from dashboard because the settings are not available on the mobile client due it being on an older version without
     * those settings OR specific settings that are not supported on the given vertical.
     */
    _classPrivateMethodInitSpec(this, _getSettingsNotAvailableOnMobileClient);
    _classPrivateMethodInitSpec(this, _getSettingsNotAvailableInCurrentCountry);
    _classPrivateMethodInitSpec(this, _getAddonSettingsNotAvailableOnCurrentSubscription);
    _classPrivateMethodInitSpec(this, _getAddonSettingsNotAvailableOnMobileClient);
    _defineProperty(this, "profileType", DeviceProfileType.UNKNOWN);
    _defineProperty(this, "userSubscriptions", {});
    _defineProperty(this, "permissions", {});
  }
  /**
   * @return {Boolean} true if the current vertical is supported by DPv2 dashboard page. Unsupported types should not show up
   * in dashboard
   */
  get isSupported() {
    switch (this.profileType) {
      case DeviceProfileType.TERMINAL_API:
        return this.permissions.canManageTerminalApi;
      case DeviceProfileType.SQUID_KIOSK:
        return this.permissions.canUseSuperposKioskModes;
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
      case DeviceProfileType.RETAIL:
      case DeviceProfileType.APPOINTMENTS:
      case DeviceProfileType.SPOS:
      case DeviceProfileType.KDS:
      case DeviceProfileType.KDS_EXPO:
      case DeviceProfileType.FNBKIOSK:
      case DeviceProfileType.INVOICES:
        return true;
      default:
        return false;
    }
  }
  showAssignedDevicesSection(canUseDeviceProfiles) {
    return this.supportsAssignByDeviceId || this.supportsAssignByDeviceCodeId(canUseDeviceProfiles);
  }

  /**
   * @return {Boolean} true if we want to show assigned devices for a vertical
   */
  get supportsAssignByDeviceId() {
    switch (this.profileType) {
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
      case DeviceProfileType.KDS:
      case DeviceProfileType.KDS_EXPO:
        return false;
      default:
        return true;
    }
  }

  /**
   * @return {Boolean} true if we want to show assigned device codes for a vertical. Temp solution
   */
  supportsAssignByDeviceCodeId(canUseDeviceProfiles) {
    if (!canUseDeviceProfiles) {
      return false;
    }
    switch (this.profileType) {
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
        return true;
      default:
        return false;
    }
  }
  get isOrWasSubscribed() {
    switch (this.profileType) {
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
        return this.userSubscriptions.wasSubscribedToRestaurants || this.isSubscribed;
      default:
        return this.isSubscribed;
    }
  }

  /**
   * @return {Boolean} true if the current vertical is supported AND the seller matches the subscription requirements to interact with it
   */
  get isSubscribed() {
    switch (this.profileType) {
      case DeviceProfileType.RESTAURANT:
        return this.userSubscriptions.canUseRestaurants || this.userSubscriptions.canUseRestaurantsFreeTierOnly;
      case DeviceProfileType.RESTAURANT_MOBILE:
        return this.userSubscriptions.canUseRestaurants && (this.userSubscriptions.canUseRestaurantsPlus || this.userSubscriptions.canUseRestaurantsMobile);
      case DeviceProfileType.RETAIL:
        return this.userSubscriptions.canUseRetail || this.userSubscriptions.canUseRetailPlus;
      case DeviceProfileType.APPOINTMENTS:
        return this.userSubscriptions.canUseAppointments;
      case DeviceProfileType.KDS:
        return this.userSubscriptions.canUseRestaurantsPlus || this.userSubscriptions.canUseKDS;
      case DeviceProfileType.KDS_EXPO:
        return this.userSubscriptions.canUseRestaurantsPlus || this.userSubscriptions.canUseKDS || this.userSubscriptions.canUseKDSFreeTrialOnly;
      case DeviceProfileType.SPOS:
      case DeviceProfileType.TERMINAL_API:
        return true;
      case DeviceProfileType.FNBKIOSK:
      case DeviceProfileType.SQUID_KIOSK:
        return this.userSubscriptions.canUseFnbKiosk;
      case DeviceProfileType.INVOICES:
        return this.userSubscriptions.canUseIpos;
      default:
        return false;
    }
  }

  /**
   * @return {string} return customer management setting label by vertical
   */
  get customerManagementLabelTranslationKey() {
    switch (this.profileType) {
      case DeviceProfileType.RETAIL:
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
        return 'business.devices.profiles.v2.settings.checkout.customerManagement.RST.name';
      default:
        return 'business.devices.profiles.v2.settings.checkout.customerManagement.name';
    }
  }

  /**
   * @return {string} return translation key for the help text under the show email collection screen setting.
   * This translation key is meant to be used with a SqHelpLink component with the article id
   */
  get showEmailCollectionScreenHelperTranslationConfig() {
    switch (this.profileType) {
      case DeviceProfileType.RETAIL:
        return {
          key: 'business.devices.profiles.v2.settings.checkout.customerManagement.showEmailCollectionScreenHelperText.retail',
          articleId: '5744'
        };
      default:
        return {
          key: 'business.devices.profiles.v2.settings.checkout.customerManagement.showEmailCollectionScreenHelperText.default',
          articleId: '5744'
        };
    }
  }

  /**
   * @return {Array<DeviceProfileType>} Get a list of supported profile types to duplicate for the current vertical
   */
  get supportedDuplicationTypes() {
    // can always duplicate to the same profile type
    const defaultTypes = [this.profileType];
    switch (this.profileType) {
      case DeviceProfileType.SPOS:
        return [...defaultTypes, DeviceProfileType.RETAIL, DeviceProfileType.APPOINTMENTS, DeviceProfileType.RESTAURANT, DeviceProfileType.RESTAURANT_MOBILE];
      case DeviceProfileType.RESTAURANT:
        return [...defaultTypes, DeviceProfileType.RESTAURANT_MOBILE, DeviceProfileType.SPOS];
      case DeviceProfileType.RESTAURANT_MOBILE:
        return [...defaultTypes, DeviceProfileType.RESTAURANT, DeviceProfileType.SPOS];
      case DeviceProfileType.APPOINTMENTS:
      case DeviceProfileType.RETAIL:
      case DeviceProfileType.INVOICES:
        return [...defaultTypes, DeviceProfileType.SPOS];
      default:
        return defaultTypes;
    }
  }
  get excludedPaymentTypes() {
    // These are specific tenders where it is set on the server however
    // it is not supported on the client at the moment and thus need to
    // be hidden away.
    const rstExcluded = new Set([TenderSettings.TenderType.PUSH, TenderSettings.TenderType.CHECKOUT_LINK_QR_CODE, TenderSettings.TenderType.INVOICE, TenderSettings.TenderType.CHECKOUT_LINK]);
    switch (this.profileType) {
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
        return [...rstExcluded];
      default:
        return [];
    }
  }
  get showAutoGratuityTippingSettingsBanner() {
    switch (this.profileType) {
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
        return true;
      default:
        return false;
    }
  }

  /**
   * @return {String} Get the translation key for the vertical's display value
   */
  get displayTranslationKey() {
    return VerticalConfiguration.getDisplayTranslationKey(this.profileType);
  }

  /**
   * @return {String} Get the translation key for the vertical's point of sale system's display value
   * This is currently used for RST b/c RST_MOBILE and RST_TABLET are displayed under the same POS system name
   */
  get posSystemDisplayTranslationKey() {
    return VerticalConfiguration.getPosSystemDisplayTranslationKey(this.profileType);
  }

  /**
   * @return {Boolean} true if the profile type supports profile templates
   */
  get canUseDeviceProfileTemplates() {
    return VerticalConfiguration.canUseDeviceProfileTemplates(this.profileType);
  }

  /**
   * @return {ProductType} Product type enum mapped to the device profile type
   */
  get productType() {
    return VerticalConfiguration.getProductTypeForProfileType(this.profileType);
  }
  get supportsDeviceCodes() {
    return VerticalConfiguration.supportsDeviceCodes(this.profileType);
  }
  get supportsSharedProfiles() {
    switch (this.profileType) {
      case DeviceProfileType.KDS:
      case DeviceProfileType.KDS_EXPO:
        return false;
      default:
        return true;
    }
  }
  get canDefaultPrivateProfilesOnDeviceCode() {
    switch (this.profileType) {
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
      case DeviceProfileType.KDS:
      case DeviceProfileType.KDS_EXPO:
      case DeviceProfileType.FNBKIOSK:
        return true;
      default:
        return false;
    }
  }
  get isRestaurantsDevice() {
    switch (this.profileType) {
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
      case DeviceProfileType.FNBKIOSK:
      case DeviceProfileType.SQUID_KIOSK:
        return true;
      default:
        return false;
    }
  }
  get employeeTimeTrackingRequiresShiftPlus() {
    switch (this.profileType) {
      case DeviceProfileType.SPOS:
        return true;
      default:
        return false;
    }
  }
  get isUsingAOL() {
    switch (this.profileType) {
      case DeviceProfileType.SPOS:
      case DeviceProfileType.APPOINTMENTS:
      case DeviceProfileType.RETAIL:
        return true;
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
        return this.permissions.aolOnRst;
      case DeviceProfileType.INVOICES:
        return this.permissions.aolOnIpos;
      default:
        return false;
    }
  }

  /**
   * @return {Array<string>} Array of addon setting keys in the proto that should be hidden on dashboard for various reasons
   */
  getAddonSettingsNotAvailable(addonId = null) {
    const settingsNotAvailableOnMobileClient = _classPrivateMethodGet(this, _getAddonSettingsNotAvailableOnMobileClient, _getAddonSettingsNotAvailableOnMobileClient2).call(this, addonId);
    const settingsNotAvailableOnCurrentSubscription = _classPrivateMethodGet(this, _getAddonSettingsNotAvailableOnCurrentSubscription, _getAddonSettingsNotAvailableOnCurrentSubscription2).call(this, addonId);
    const settingsNotAvailable = new Set([...settingsNotAvailableOnMobileClient, ...settingsNotAvailableOnCurrentSubscription]);
    return [...settingsNotAvailable];
  }
  /**
   * @return {Array<string>} Array of setting keys in the proto that should be hidden on dashboard for various reasons
   */
  getSettingsNotAvailable(topLevelFieldName = null, countryCode = null) {
    const settingsNotAvailableOnMobileClient = _classPrivateMethodGet(this, _getSettingsNotAvailableOnMobileClient, _getSettingsNotAvailableOnMobileClient2).call(this, topLevelFieldName);
    const settingsNotAvailableOnCurrentSubscription = _classPrivateMethodGet(this, _getSettingsNotAvailableOnCurrentSubscription, _getSettingsNotAvailableOnCurrentSubscription2).call(this, topLevelFieldName);
    const settingsNotAvailableInCurrentCountry = _classPrivateMethodGet(this, _getSettingsNotAvailableInCurrentCountry, _getSettingsNotAvailableInCurrentCountry2).call(this, topLevelFieldName, countryCode);
    const settingsNotAvailable = new Set([...settingsNotAvailableOnMobileClient, ...settingsNotAvailableOnCurrentSubscription, ...settingsNotAvailableInCurrentCountry]);
    return [...settingsNotAvailable];
  }
  bundleNameTranslationKey(addonId) {
    // Source: https://docs.google.com/document/d/1fK_ePWV42VaYPOu8Mq53YlJotKnDDou6k31FjMLkMpM
    // TODO: Move this somewhere it can be reused
    const rstBaseline = [FirstPartyAddon.CASH_MANAGEMENT, FirstPartyAddon.OPEN_TICKETS];
    const rstPlus = [FirstPartyAddon.GIFT_CARD, FirstPartyAddon.LOYALTY, FirstPartyAddon.TEAM_MANAGEMENT];
    const rtlBaseline = [FirstPartyAddon.OPEN_TICKETS];
    const rtlPlus = [FirstPartyAddon.CASH_MANAGEMENT, FirstPartyAddon.GIFT_CARD, FirstPartyAddon.INVOICES, FirstPartyAddon.LOYALTY, FirstPartyAddon.MESSAGES, FirstPartyAddon.ONLINE_CHECKOUT, FirstPartyAddon.BUYER_SUBSCRIPTIONS, FirstPartyAddon.TEAM_MANAGEMENT, FirstPartyAddon.TEXT_MESSAGE_MARKETING];
    const aposBaseline = [FirstPartyAddon.CASH_MANAGEMENT, FirstPartyAddon.OPEN_TICKETS, FirstPartyAddon.GIFT_CARD, FirstPartyAddon.INVOICES, FirstPartyAddon.LOYALTY, FirstPartyAddon.MESSAGES, FirstPartyAddon.BUYER_SUBSCRIPTIONS, FirstPartyAddon.TEAM_MANAGEMENT, FirstPartyAddon.TEXT_MESSAGE_MARKETING];
    switch (this.profileType) {
      case DeviceProfileType.RESTAURANT_MOBILE:
      case DeviceProfileType.RESTAURANT:
        if (rstBaseline.includes(addonId)) {
          return 'business.devices.profiles.v2.common.paidPlanName.restaurants.free';
        } else if (rstPlus.includes(addonId)) {
          return 'business.devices.profiles.v2.common.paidPlanName.restaurants.plus';
        }
        return '';
      case DeviceProfileType.RETAIL:
        if (rtlBaseline.includes(addonId)) {
          return 'business.devices.profiles.v2.common.paidPlanName.retail.free';
        } else if (rtlPlus.includes(addonId)) {
          return 'business.devices.profiles.v2.common.paidPlanName.retail.freeAndPlus';
        }
        return '';
      case DeviceProfileType.APPOINTMENTS:
        if (aposBaseline.includes(addonId)) {
          return 'business.devices.profiles.v2.common.paidPlanName.appointments';
        }
        return '';
      default:
        return '';
    }
  }

  /**
   *
   * @param {FirstPartyAddon} addonId
   * @returns whether addon can be disabled or not. The toggle to control enablement is disabled in cases where an addon is considered included as
   * part of a subscription. If a vertical onboards to AOL, then this should return `true` so that it reflects mobile client behavior.
   */
  addonCanBeDisabled(addonId) {
    // Addons where the toggle to control enablement should be disabled
    const rstAddons = [FirstPartyAddon.TEAM_MANAGEMENT];
    const appointmentsAddons = [FirstPartyAddon.INVOICES, FirstPartyAddon.MESSAGES, FirstPartyAddon.BUYER_SUBSCRIPTIONS];
    const rtlAddons = [FirstPartyAddon.OPEN_TICKETS];
    switch (this.profileType) {
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
        return this.isUsingAOL || !rstAddons.includes(addonId);
      case DeviceProfileType.APPOINTMENTS:
        return this.isUsingAOL || !appointmentsAddons.includes(addonId);
      case DeviceProfileType.RETAIL:
        return this.isUsingAOL || !rtlAddons.includes(addonId);
      default:
        return true;
    }
  }

  /**
   *
   * @param {FirstPartyAddon} addonId
   * @returns vertical specific name translationKey for an addon if defined else null
   */
  addonCustomNameTranslationKey(addonId) {
    const rstNaming = {
      [FirstPartyAddon.OPEN_TICKETS]: 'business.devices.profiles.v2.addons.openTickets.RST.label',
      [FirstPartyAddon.TEAM_MANAGEMENT]: 'business.devices.profiles.v2.addons.teamManagement.RST.label'
    };
    const appointmentsNaming = {
      [FirstPartyAddon.TEAM_MANAGEMENT]: 'business.devices.profiles.v2.addons.teamManagement.APT.label'
    };
    const rtlNaming = {
      [FirstPartyAddon.OPEN_TICKETS]: 'business.devices.profiles.v2.addons.openTickets.RTL.label',
      [FirstPartyAddon.TEAM_MANAGEMENT]: 'business.devices.profiles.v2.addons.teamManagement.RTL.label'
    };
    switch (this.profileType) {
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
        return rstNaming[addonId];
      case DeviceProfileType.APPOINTMENTS:
        return appointmentsNaming[addonId];
      case DeviceProfileType.RETAIL:
        return rtlNaming[addonId];
      default:
        return null;
    }
  }
  static canUseDeviceProfileTemplates(profileType) {
    switch (profileType) {
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
        return true;
      default:
        return false;
    }
  }

  /**
   *
   * @param {ProductType} productType - Enum in ProductType proto
   * @returns {Boolean} true if the current vertical supports
   * showing passcode screen during device code creation.
   */
  static showOwnerPasscodeScreen(productType) {
    switch (productType) {
      case ProductType.RESTAURANT:
      case ProductType.RESTAURANT_MOBILE:
        return true;
      default:
        return false;
    }
  }

  // Add more props here
  /**
   * @param {ProductType} - Enum in ProductType proto
   * @return {DeviceProfileType} Get the profile type mapping for the given device code product type proto value
   */
  static getProfileTypeForProductType(productType) {
    // Add more props here
    switch (productType) {
      case ProductType.UNKNOWN:
      case ProductType.REGISTER:
        return DeviceProfileType.SPOS;
      case ProductType.KDS:
        return DeviceProfileType.KDS;
      case ProductType.KDS_EXPO:
        return DeviceProfileType.KDS_EXPO;
      case ProductType.RESTAURANT:
        return DeviceProfileType.RESTAURANT;
      case ProductType.RESTAURANT_MOBILE:
        return DeviceProfileType.RESTAURANT_MOBILE;
      case ProductType.TERMINAL_API:
        return DeviceProfileType.TERMINAL_API;
      case ProductType.RETAIL:
        return DeviceProfileType.RETAIL;
      case ProductType.FNBKIOSK:
        return DeviceProfileType.FNBKIOSK;
      case ProductType.SQUID_KIOSK:
        return DeviceProfileType.SQUID_KIOSK;
      case ProductType.APPOINTMENTS:
        return DeviceProfileType.APPOINTMENTS;
      case ProductType.INVOICES:
        return DeviceProfileType.INVOICES;
      default:
        throw new Error(`Unknown product type ${productType}`);
    }
  }

  /**
   * @param {DeviceProfileType} - Enum in DeviceProfileType proto
   * @return {ProductType} - Enum in ProductType proto
   */
  static getProductTypeForProfileType(deviceProfileType) {
    // Add more props here
    switch (deviceProfileType) {
      case DeviceProfileType.SPOS:
        return ProductType.REGISTER;
      case DeviceProfileType.KDS:
        return ProductType.KDS;
      case DeviceProfileType.KDS_EXPO:
        return ProductType.KDS_EXPO;
      case DeviceProfileType.RESTAURANT_MOBILE:
        return ProductType.RESTAURANT_MOBILE;
      case DeviceProfileType.RESTAURANT:
        return ProductType.RESTAURANT;
      case DeviceProfileType.TERMINAL_API:
        return ProductType.TERMINAL_API;
      case DeviceProfileType.RETAIL:
        return ProductType.RETAIL;
      case DeviceProfileType.FNBKIOSK:
        return ProductType.FNBKIOSK;
      case DeviceProfileType.SQUID_KIOSK:
        return ProductType.SQUID_KIOSK;
      case DeviceProfileType.APPOINTMENTS:
        return ProductType.APPOINTMENTS;
      case DeviceProfileType.INVOICES:
        return ProductType.INVOICES;
      default:
        throw new Error(`Unknown profile type ${deviceProfileType}`);
    }
  }

  /**
   * @param {DeviceProduct} - Enum in Device.Product proto
   * @return {DeviceProfileType} Get the profile type mapping for the given client product proto value
   */
  static getProfileTypeForClientProductType(product) {
    switch (product) {
      case DeviceProduct.SQUARE:
        return DeviceProfileType.SPOS;
      case DeviceProduct.REGISTER:
        return DeviceProfileType.SPOS;
      case DeviceProduct.RETAIL:
        return DeviceProfileType.RETAIL;
      case DeviceProduct.KDS:
        return DeviceProfileType.KDS_EXPO;
      case DeviceProduct.KDS_EXPO:
        return DeviceProfileType.KDS_EXPO;
      case DeviceProduct.RESTAURANT:
        return DeviceProfileType.RESTAURANT;
      case DeviceProduct.RESTAURANT_MOBILE:
        return DeviceProfileType.RESTAURANT_MOBILE;
      case DeviceProduct.APPOINTMENTS:
        return DeviceProfileType.APPOINTMENTS;
      case DeviceProduct.TERMINAL_API:
        return DeviceProfileType.TERMINAL_API;
      case DeviceProduct.INVOICES:
        return DeviceProfileType.INVOICES;
      default:
        throw new Error(`Unknown client product type ${product}`);
    }
  }

  /**
   *
   * @param {DeviceProfileType} profileType - profile type
   * @returns corresponding device hub device application type
   */
  static getDeviceHubApplicationTypeFromProfileType(profileType) {
    switch (profileType) {
      case DeviceProfileType.RESTAURANT:
        return ApplicationType.RESTAURANT;
      case DeviceProfileType.RESTAURANT_MOBILE:
        return ApplicationType.RESTAURANT;
      case DeviceProfileType.KDS:
        return ApplicationType.KDS;
      case DeviceProfileType.KDS_EXPO:
        return ApplicationType.KDS;
      case DeviceProfileType.FNBKIOSK:
        return ApplicationType.FNB_KIOSK;
      case DeviceProfileType.SQUID_KIOSK:
        return ApplicationType.SQUID_KIOSK;
      case DeviceProfileType.SPOS:
        return ApplicationType.SQUARE_POS;
      case DeviceProfileType.APPOINTMENTS:
        return ApplicationType.APPOINTMENTS;
      case DeviceProfileType.RETAIL:
        return ApplicationType.RETAIL;
      case DeviceProfileType.TERMINAL_API:
        return ApplicationType.TERMINAL_API;
      case DeviceProfileType.INVOICES:
        return ApplicationType.INVOICES;
      default:
        throw new Error(`Unknown profile type ${profileType}`);
    }
  }

  /**
   *
   * @param {DeviceProfileType} profileType - profile type
   * @returns corresponding device hub device application type that doesn't support superpos modes
   */
  static getNonSuperposSupportedApplicationFromProfileType(profileType) {
    switch (profileType) {
      case DeviceProfileType.KDS:
        return ApplicationType.KDS;
      case DeviceProfileType.KDS_EXPO:
        return ApplicationType.KDS;
      case DeviceProfileType.FNBKIOSK:
        return ApplicationType.FNB_KIOSK;
      case DeviceProfileType.SQUID_KIOSK:
        return ApplicationType.SQUID_KIOSK;
      case DeviceProfileType.TERMINAL_API:
        return ApplicationType.TERMINAL_API;
      default:
        return null;
    }
  }

  /**
   * @param {DeviceProfileType} - Profile type to get translation key
   * @param {KDSFetchSettings.StationType} - Optional station type to differentiate between different types of KDS
   * @return {String} Get the translation key for the vertical's display value
   */
  static getDisplayTranslationKey(profileType, stationType) {
    switch (profileType) {
      case DeviceProfileType.RETAIL:
        return 'business.devices.profiles.v2.verboseVerticalNames.RETAIL';
      case DeviceProfileType.RESTAURANT:
        return 'business.devices.profiles.v2.verboseVerticalNames.RESTAURANT';
      case DeviceProfileType.RESTAURANT_MOBILE:
        return 'business.devices.profiles.v2.verboseVerticalNames.RESTAURANT_MOBILE';
      case DeviceProfileType.TERMINAL_API:
        return 'business.devices.profiles.v2.verboseVerticalNames.TERMINAL_API';
      case DeviceProfileType.KDS:
        if (stationType === KDSFetchSettings.StationType.FIRST_STAGE_EXPO) {
          return 'business.devices.profiles.v2.verboseVerticalNames.KDS_EXPO';
        }
        return 'business.devices.profiles.v2.verboseVerticalNames.KDS';
      case DeviceProfileType.KDS_EXPO:
        return 'business.devices.profiles.v2.verboseVerticalNames.KDS_EXPO';
      case DeviceProfileType.SQUARE_HARDWARE:
        return 'business.devices.profiles.v2.verboseVerticalNames.SQUARE_HARDWARE';
      case DeviceProfileType.APPOINTMENTS:
        return 'business.devices.profiles.v2.verboseVerticalNames.APPOINTMENTS';
      case DeviceProfileType.SPOS:
        return 'business.devices.profiles.v2.verboseVerticalNames.SPOS';
      case DeviceProfileType.RELEASE_MANAGER:
        return 'business.devices.profiles.v2.verboseVerticalNames.RELEASE_MANAGER';
      case DeviceProfileType.FNBKIOSK:
        return 'business.devices.profiles.v2.verboseVerticalNames.FNBKIOSK';
      case DeviceProfileType.SQUID_KIOSK:
        return 'business.devices.profiles.v2.verboseVerticalNames.SQUID_KIOSK';
      case DeviceProfileType.INVOICES:
        return 'business.devices.profiles.v2.verboseVerticalNames.INVOICES';
      default:
        throw new Error(`Unknown profile type ${profileType}`);
    }
  }

  /**
   * @param {DeviceProfileType} - Profile type to get translation key
   * @return {String} Get the translation key for the vertical's point of sale system's display value
   * This is currently used for RST b/c RST_MOBILE and RST_TABLET are displayed under the same POS system name
   */
  static getPosSystemDisplayTranslationKey(profileType) {
    switch (profileType) {
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
        return 'business.devices.profiles.v2.verbosePosVerticalNames.RESTAURANT';
      default:
        return VerticalConfiguration.getDisplayTranslationKey(profileType);
    }
  }

  /**
   * @param {ProductType} - Enum in ProductType proto
   * @return {String} Get the translation key for the vertical's display value
   */
  static getDisplayTranslationKeyForProductType(productType) {
    switch (productType) {
      case ProductType.RETAIL:
        return 'business.devices.profiles.v2.verboseVerticalNames.RETAIL';
      case ProductType.RESTAURANT:
        return 'business.devices.profiles.v2.verboseVerticalNames.RESTAURANT';
      case ProductType.RESTAURANT_MOBILE:
        return 'business.devices.profiles.v2.verboseVerticalNames.RESTAURANT_MOBILE';
      case ProductType.TERMINAL_API:
        return 'business.devices.profiles.v2.verboseVerticalNames.TERMINAL_API';
      case ProductType.KDS:
        return 'business.devices.profiles.v2.verboseVerticalNames.KDS';
      case ProductType.KDS_EXPO:
        return 'business.devices.profiles.v2.verboseVerticalNames.KDS_EXPO';
      case ProductType.FNBKIOSK:
        return 'business.devices.profiles.v2.verboseVerticalNames.FNBKIOSK';
      case ProductType.SQUID_KIOSK:
        return 'business.devices.profiles.v2.verboseVerticalNames.SQUID_KIOSK';
      case ProductType.APPOINTMENTS:
        return 'business.devices.profiles.v2.verboseVerticalNames.APPOINTMENTS';
      case ProductType.INVOICES:
        return 'business.devices.profiles.v2.verboseVerticalNames.INVOICES';
      case ProductType.UNKNOWN:
      case ProductType.REGISTER:
        return 'business.devices.profiles.v2.verboseVerticalNames.SPOS';
      default:
        throw new Error(`Unknown product type ${productType}`);
    }
  }

  /**
   *
   * @param {ProductType} productType Enum in product type proto for device codes.
   * @return {String} The translation key for the vertical's unassigned settings subtext on the manage settings screen in the device code create flow.
   */
  static getManageSettingsUnassignedSettingsSubtextTranslationKey(productType) {
    switch (productType) {
      case ProductType.KDS:
      case ProductType.KDS_EXPO:
        return 'business.devices.profiles.v2.createDeviceCode.manageSettings.unassignedSettingsSubtextKDS';
      default:
        return 'business.devices.profiles.v2.createDeviceCode.manageSettings.unassignedSettingsSubtextDefault';
    }
  }

  /**
   *
   * @param {ProductType} productType Enum in product type proto for device codes.
   * @return {String} The translation key for the vertical's assigned to profile subtext on the manage settings screen in the device code create flow.
   */
  static getManageSettingsAssignedToProfileSubtextTranslationKey(productType) {
    switch (productType) {
      case ProductType.KDS:
      case ProductType.KDS_EXPO:
        return 'business.devices.profiles.v2.createDeviceCode.manageSettings.assignedToProfileSubtextKDS';
      default:
        return 'business.devices.profiles.v2.createDeviceCode.manageSettings.assignedToProfileSubtextDefault';
    }
  }
  static supportsDeviceCodes(profileType) {
    switch (profileType) {
      case DeviceProfileType.SPOS:
      case DeviceProfileType.RESTAURANT:
      case DeviceProfileType.RESTAURANT_MOBILE:
      case DeviceProfileType.RETAIL:
      case DeviceProfileType.KDS:
      case DeviceProfileType.KDS_EXPO:
      case DeviceProfileType.FNBKIOSK:
      case DeviceProfileType.SQUID_KIOSK:
      case DeviceProfileType.TERMINAL_API:
      case DeviceProfileType.INVOICES:
      case DeviceProfileType.APPOINTMENTS:
        return true;
      default:
        return false;
    }
  }
}
function _getAddonSettingsNotAvailableOnMobileClient2(addonId = null) {
  const rstAddonSettingsNotAvailableOnMobileClient = {
    [FirstPartyAddon.OPEN_TICKETS]: ['usesPredefinedTickets',
    // For RST it is expected to manage Pre-authorization settings from Device management > Service settings > Pre Authorization,
    // for this reason Pre-auth settings within device profile should be hidden.
    'isPreAuthEnabled']
  };
  const rtlAddonsNotAvailableOnMobileClient = [FirstPartyAddon[FirstPartyAddon.OPEN_TICKETS]];
  switch (this.profileType) {
    case DeviceProfileType.RESTAURANT:
    case DeviceProfileType.RESTAURANT_MOBILE:
      return addonId ? rstAddonSettingsNotAvailableOnMobileClient[addonId] || [] : [];
    case DeviceProfileType.RETAIL:
      return addonId ? [] : rtlAddonsNotAvailableOnMobileClient;
    default:
      return [];
  }
}
function _getAddonSettingsNotAvailableOnCurrentSubscription2(_addonId = null) {
  switch (this.profileType) {
    default:
      return [];
  }
}
function _getSettingsNotAvailableInCurrentCountry2(topLevelFieldName = null, countryCode = null) {
  if (topLevelFieldName === null || countryCode === null) {
    return [];
  }
  const baseSignatureSettingsNotAvailable = ['collectSignatures', 'signatureThreshold', 'signatureLocation'];
  const baseSettingsNotAvailableByCountry = {
    AU: {
      signature: baseSignatureSettingsNotAvailable,
      tipping: ['preAuthTipping']
    },
    GB: {
      signature: baseSignatureSettingsNotAvailable,
      tipping: ['preAuthTipping']
    },
    FR: {
      signature: baseSignatureSettingsNotAvailable,
      tipping: ['preAuthTipping']
    },
    ES: {
      signature: baseSignatureSettingsNotAvailable,
      tipping: ['preAuthTipping']
    },
    IE: {
      signature: baseSignatureSettingsNotAvailable,
      tipping: ['preAuthTipping']
    },
    JP: {
      signature: ['collectSignatures', 'signatureThreshold'],
      tipping: ['preAuthTipping']
    },
    CA: {
      tipping: ['preAuthTipping']
    }
  };
  const rtlSettingsNotAvailableByCountry = {
    ...baseSettingsNotAvailableByCountry,
    JP: {
      signature: ['signatureThreshold']
    }
  };
  switch (this.profileType) {
    case DeviceProfileType.SPOS:
    case DeviceProfileType.RESTAURANT:
    case DeviceProfileType.RESTAURANT_MOBILE:
    case DeviceProfileType.APPOINTMENTS:
      {
        return baseSettingsNotAvailableByCountry[countryCode]?.[topLevelFieldName] ?? [];
      }
    case DeviceProfileType.RETAIL:
      {
        return rtlSettingsNotAvailableByCountry[countryCode]?.[topLevelFieldName] ?? [];
      }
    default:
      return [];
  }
}
function _getSettingsNotAvailableOnMobileClient2(topLevelFieldName = null) {
  // currently hidden until dpv2 integration on the client is implemented
  const rstSettingsNotAvailableOnMobileClient = ['quickAmount', 'textCustomer' // temp hidden until we get confirmation on expected visibility
  ];
  const rstSubSettingsNotAvailableOnMobileClient = {
    security: ['passcodesEnabled', 'allowTeamPasscode', 'requirePasscodeAfterLogout'],
    paymentTypes: ['skipReviewSale']
  };
  const rstHandheldSettingsNotAvailableOnMobileClient = {
    ...rstSubSettingsNotAvailableOnMobileClient,
    security: [...rstSubSettingsNotAvailableOnMobileClient.security,
    // Should not be present for Handheld profiles since it has no effect there.
    'requirePasscodeWhenBackingOutOfBuyerFacingScreen'],
    floorPlan: ['showFloorPlanTimers']
  };
  const kdsSettingsNotAvailableOnMobileClient = ['security'];
  const kioskSettingsNotAvailableOnMobileClient = ['security'];
  const iposSettingsNotAvailableOnMobileClient = ['buyerFacingDisplay', 'orderTickets', 'paymentSounds'];

  // ipos can't see these setings if they can't use superpos
  if (!this.permissions.canUseSuperpos) {
    iposSettingsNotAvailableOnMobileClient.push('security', 'offlineMode');
  }
  switch (this.profileType) {
    case DeviceProfileType.RESTAURANT:
      return topLevelFieldName ? rstSubSettingsNotAvailableOnMobileClient[topLevelFieldName] || [] : rstSettingsNotAvailableOnMobileClient;
    case DeviceProfileType.RESTAURANT_MOBILE:
      return topLevelFieldName ? rstHandheldSettingsNotAvailableOnMobileClient[topLevelFieldName] || [] : rstSettingsNotAvailableOnMobileClient;
    case DeviceProfileType.KDS:
    case DeviceProfileType.KDS_EXPO:
      return topLevelFieldName ? [] : kdsSettingsNotAvailableOnMobileClient;
    case DeviceProfileType.FNBKIOSK:
      return topLevelFieldName ? [] : kioskSettingsNotAvailableOnMobileClient;
    case DeviceProfileType.INVOICES:
      return topLevelFieldName ? [] : iposSettingsNotAvailableOnMobileClient;
    default:
      return [];
  }
}
function _getSettingsNotAvailableOnCurrentSubscription2(topLevelFieldName = null) {
  const rstSettingsNotAvailableOnFree = ['appearance'];
  const rstSubSettingsNotAvailableOnFree = {
    floorPlan: ['useTableInactivityIndicators', 'tableIndicatorWarningTime', 'tableIndicatorAlertTime']
  };
  switch (this.profileType) {
    case DeviceProfileType.RESTAURANT:
    case DeviceProfileType.RESTAURANT_MOBILE:
      // can possibly be moved to a new field
      if (!this.userSubscriptions.canUseRestaurantsPlus && !this.userSubscriptions.canAccessSquareOne) {
        return topLevelFieldName ? rstSubSettingsNotAvailableOnFree[topLevelFieldName] || [] : rstSettingsNotAvailableOnFree;
      }
      return [];
    default:
      return [];
  }
}